import React, { useEffect, useState } from 'react';
import Modal from 'components/elements/Modal';
import * as yup from 'yup';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { toast } from 'react-toastify';
import {
  Box,
  Button,
  DialogActions,
  TextField,
  Grid,
  Checkbox,
  FormControlLabel
} from '@mui/material';
import { LocalizationProvider, TimePicker } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { colours, GetNonServerErrorMessage } from 'utils/constants';
import SpaceService from 'services/hooks/space-service';
import useManageOccupancy from './crud/useManageCurrentOccupancy';
import { useQuery, useQueryClient } from 'react-query';
import { generateApiFailErrorMessage } from 'utils/helpers';
import BuildingService, { BuildingProps } from 'services/hooks/building-service';
import { utcToZonedTime, zonedTimeToUtc } from 'date-fns-tz';
import { HelpTooltipIcon } from 'components/elements/Help/HelpTooltipIcon';
import InfoBox from 'utils/InfoBox';

interface ManageOccupancyFormModalProps {
  open: boolean;
  currentCount: number;
  handleClose: () => void;
  selectedBuildingUuid: string;
  selectedBuildingTimezone: string;
}

interface FormValues {
  actualCount: number | null;
  resetTime: Date | undefined;
}

const ManageOccupancyFormModal: React.FC<ManageOccupancyFormModalProps> = ({
  open,
  currentCount,
  handleClose,
  selectedBuildingUuid,
  selectedBuildingTimezone
}) => {
  const queryClient = useQueryClient();
  const defaultResetTime = new Date();
  defaultResetTime.setHours(2, 0, 0, 0); // Set default time to 2:00 AM

  const [includeResetTime, setIncludeResetTime] = useState<boolean>(false);
  const [disabled, setDisabled] = useState<boolean>(false);

  const ManageOccupancySchema = yup.object().shape({
    actualCount: yup
      .number()
      .transform((value, originalValue) => (originalValue === '' ? null : value))
      .nullable()
      .typeError('Actual Count must be a number')
      .min(0, 'Actual Count cannot be negative'),
    resetTime: yup
      .date()
      .nullable()
      .test('is-required', 'Reset Time is required when auto-reset is enabled', function (value) {
        return includeResetTime ? !!value : true;
      })
  });

  const { data: buildingData } = useQuery<BuildingProps, Error>(
    ['buildingResetTime', selectedBuildingUuid, open],
    () => BuildingService.findBuildingById(selectedBuildingUuid),
    {
      enabled: !!selectedBuildingUuid && !!open,
      onError: () => {
        toast.error(generateApiFailErrorMessage('Building'));
      }
    }
  );

  const methods = useForm<FormValues>({
    defaultValues: {
      actualCount: null,
      resetTime: defaultResetTime
    },
    resolver: yupResolver(ManageOccupancySchema)
  });

  useEffect(() => {
    if (buildingData?.applessCounterResetTime) {
      const resetTimeInUTC = new Date(buildingData.applessCounterResetTime);
      const resetTimeInBuildingTimezone = utcToZonedTime(resetTimeInUTC, selectedBuildingTimezone);
      methods.setValue('resetTime', resetTimeInBuildingTimezone);
      setIncludeResetTime(true);
    }
  }, [buildingData, methods, selectedBuildingTimezone]);

  const resetFormState = () => {
    setIncludeResetTime(false);
    methods.reset();
    queryClient.invalidateQueries('buildingResetTime');
  };

  const manageOccupancyMutation = useManageOccupancy({
    manageCurrentOccupancy: ({ buildingUuid, payload }) =>
      SpaceService.manageCurrentOccupancy(buildingUuid, payload),
    queryName: 'manageCurrentOccupancy',
    onSuccess: () => {
      toast.success('Occupancy settings saved successfully!');
      handleClose();
      setDisabled(false);
      resetFormState();
    },
    onError: (error: any) => {
      setDisabled(false);
      console.log('error::', error);
      if (error.response) {
        toast.error('Error saving occupancy settings!');
      } else {
        toast.error(GetNonServerErrorMessage);
      }
    }
  });

  const onSubmit = async (data: FormValues) => {
    setDisabled(true);
    const resetTimeInUTC =
      includeResetTime && data.resetTime
        ? zonedTimeToUtc(data.resetTime, selectedBuildingTimezone)
        : null;

    const payload = {
      actualCount: data.actualCount,
      resetTime: resetTimeInUTC
    };
    manageOccupancyMutation.mutate({ buildingUuid: selectedBuildingUuid, payload });
  };

  return (
    <Modal
      isOpen={open}
      onRequestClose={() => {
        handleClose();
        resetFormState();
      }}
      title="Manage Current Occupancy"
    >
      <form onSubmit={methods.handleSubmit(onSubmit)} className={`text-left items-start`}>
        <InfoBox message="Update the current occupancy count if it is inaccurate. Additionally, you can schedule a daily reset to automatically reset the count." />
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Grid container alignItems="center" justifyContent="flex-start">
            <Grid item xs={12} sm={4}>
              <Box sx={{ display: 'flex', alignItems: 'center', pr: 2 }}>
                <Box sx={{ textAlign: 'left', pr: 1 }}>Current Count:</Box>
                <HelpTooltipIcon
                  message="Number of people detected by the Lidar sensors."
                  useSubTag={false}
                />
              </Box>
            </Grid>
            <Grid item xs={12} sm={8}>
              <TextField
                fullWidth
                value={currentCount}
                InputProps={{
                  readOnly: true,
                  sx: {
                    maxWidth: '250px',
                    width: '100%',
                    '& .MuiInputBase-input': {
                      fontWeight: 'bold'
                    },
                    '& .MuiOutlinedInput-notchedOutline': {
                      border: 'none'
                    }
                  }
                }}
                variant="outlined"
              />
            </Grid>
          </Grid>
          <Grid container alignItems="center" justifyContent="flex-start">
            <Grid item xs={12} sm={4}>
              <Box sx={{ display: 'flex', alignItems: 'center', pr: 2 }}>
                <Box sx={{ textAlign: 'left', pr: 1 }}>Actual Count:</Box>
                <HelpTooltipIcon message="Number of people in the building." useSubTag={false} />
              </Box>
            </Grid>
            <Grid item xs={12} sm={8}>
              <Controller
                name="actualCount"
                control={methods.control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    variant="outlined"
                    type="number"
                    placeholder="55"
                    value={field.value === null ? '' : field.value}
                    onChange={(e) =>
                      field.onChange(e.target.value === '' ? null : Number(e.target.value))
                    }
                    error={!!methods.formState.errors.actualCount}
                    helperText={methods.formState.errors.actualCount?.message}
                    sx={{
                      maxWidth: '250px',
                      width: '100%'
                    }}
                  />
                )}
              />
            </Grid>
          </Grid>
          <Grid container alignItems="center" justifyContent="flex-start">
            <Grid item xs={12} sm={4}>
              <Box sx={{ display: 'flex', alignItems: 'center', pr: 2 }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={includeResetTime}
                      onChange={(e) => setIncludeResetTime(e.target.checked)}
                    />
                  }
                  label="Auto reset each day at:"
                  sx={{ mr: 1 }}
                />
                <HelpTooltipIcon
                  message="Enable this option to automatically reset the occupancy count to zero at the specified time each day."
                  useSubTag={false}
                />
              </Box>
            </Grid>
            <Grid item xs={12} sm={8}>
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <Controller
                  name="resetTime"
                  control={methods.control}
                  render={({ field }) => (
                    <TimePicker
                      value={field.value}
                      onChange={(time) => field.onChange(time)}
                      slots={{
                        textField: TextField
                      }}
                      slotProps={{
                        textField: {
                          fullWidth: true,
                          error: !!methods.formState.errors.resetTime,
                          helperText: methods.formState.errors.resetTime?.message,
                          sx: {
                            maxWidth: '250px',
                            width: '100%'
                          }
                        }
                      }}
                      disabled={!includeResetTime}
                    />
                  )}
                />
              </LocalizationProvider>
            </Grid>
          </Grid>
        </Box>
        <DialogActions
          sx={{
            justifyContent: 'flex-start',
            marginTop: '30px',
            paddingBottom: '0px',
            paddingLeft: '0px'
          }}
        >
          <Button
            onClick={() => {
              handleClose();
              resetFormState();
            }}
            variant="contained"
            size="medium"
            sx={{
              mr: 1,
              textTransform: 'none',
              fontSize: '1rem',
              backgroundColor: colours.backButtonGrey1,
              color: '#000',
              boxShadow: 'none',
              borderRadius: '8px',
              width: '90px',
              '&:hover': { backgroundColor: colours.backButtonGrey1, boxShadow: 'none' }
            }}
          >
            Discard
          </Button>
          <Button
            type="submit"
            variant="contained"
            size="medium"
            disabled={disabled}
            sx={{
              textTransform: 'none',
              fontSize: '1rem',
              backgroundColor: colours.seafoam,
              color: '#fff',
              boxShadow: 'none',
              borderRadius: '8px',
              width: '80px',
              '&:hover': { backgroundColor: colours.seafoam, boxShadow: 'none' }
            }}
          >
            Save
          </Button>
        </DialogActions>
      </form>
    </Modal>
  );
};

export default ManageOccupancyFormModal;
